/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { forwardRef, type ComponentProps, useState, useCallback, useMemo, useEffect, lazy, Suspense } from 'react';
import { Link } from '@/components/link';

import type { ShopperSearch } from '@/scapi';
import type { ComponentDesignMetadata } from '@salesforce/storefront-next-runtime/design/react';

import { cn } from '@/lib/utils';
import {
    createProductUrl,
    getDecoratedVariationAttributes,
    getImagesForColor,
    type DecoratedVariationAttributeValue,
} from '@/lib/product/product-utils';
import { getProductRating } from '@/lib/product/product-utils-plp';
import { useProductTileContext } from './context';
import { DeferredWishlistButton } from './deferred-wishlist-button';
import { PickupIcon } from '@/components/icons';
import { QuickAddButton } from './quick-add-button';
import { ProductTileSwatchesSkeleton } from '@/components/category-skeleton';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import type { ComponentType } from '@/components/region';
import { ProductImageContainer } from '@/components/product-image';
import ProductPrice from '@/components/product-price';
import CheckPriceCta from '@/components/product-price/check-price-cta';
import { TriplePlayCaption } from '@/components/product-price/triple-play-caption';
import { StarRating } from '@/components/product-ratings/star-rating';
import { UITarget } from '@/targets/ui-target';
import Badge from '@/components/badge';
import { loader as loaders } from './loaders';

const LazySwatches = lazy(() => import('./swatches').then((m) => ({ default: m.ProductTileSwatches })));

const PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID = 'color';
const PRODUCT_TILE_MAX_SWATCHES = 4;

/* v8 ignore start - do not test decorators in unit tests, decorator functionality is tested separately*/
@Component('productTile', {
    name: 'Product Tile',
    description: 'Configurable product tile with customizable styling for images, typography, and hover effects',
    group: 'Content',
})
@RegionDefinition([])
export class ProductTileMetadata {
    @AttributeDefinition({
        id: 'productId',
        name: 'Product',
        description: 'Select a product to render in this tile.',
        type: 'product',
    })
    productId?: string;

    @AttributeDefinition({
        id: 'objectFit',
        name: 'Image Object Fit',
        description: 'How the product image should fit within its container',
        type: 'enum',
        values: ['contain', 'cover', 'fill', 'none', 'scale-down'],
        defaultValue: 'contain',
    })
    objectFit?: string;

    @AttributeDefinition({
        id: 'borderRadius',
        name: 'Border Radius',
        description: 'Corner roundness of the tile card',
        type: 'enum',
        values: ['none', 'xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', 'full'],
        defaultValue: 'xl',
    })
    borderRadius?: string;

    @AttributeDefinition({
        id: 'boxShadow',
        name: 'Box Shadow',
        description: 'Shadow effect for the tile card',
        type: 'enum',
        values: ['none', 'sm', 'md', 'lg', 'xl', '2xl'],
        defaultValue: 'sm',
    })
    boxShadow?: string;

    @AttributeDefinition({
        id: 'padding',
        name: 'Padding',
        description: 'Padding on all sides of the tile',
        type: 'enum',
        values: ['0', '2', '4', '6', '8'],
        defaultValue: '0',
    })
    padding?: string;

    @AttributeDefinition({
        id: 'margin',
        name: 'Margin',
        description: 'Margin on all sides of the tile',
        type: 'enum',
        values: ['0', '2', '4', '6', '8'],
        defaultValue: '0',
    })
    margin?: string;

    @AttributeDefinition({
        id: 'fontWeight',
        name: 'Font Weight',
        description: 'Weight of the product name text',
        type: 'enum',
        values: ['normal', 'medium', 'semibold', 'bold'],
        defaultValue: 'semibold',
    })
    fontWeight?: string;

    @AttributeDefinition({
        id: 'letterSpacing',
        name: 'Letter Spacing',
        description: 'Spacing between letters in product name',
        type: 'enum',
        values: ['tighter', 'tight', 'normal', 'wide', 'wider'],
        defaultValue: 'normal',
    })
    letterSpacing?: string;

    @AttributeDefinition({
        id: 'hoverEffect',
        name: 'Hover Effect',
        description: 'Interactive hover effect for the tile',
        type: 'enum',
        values: ['default', 'scale', 'shadow', 'lift'],
        defaultValue: 'default',
    })
    hoverEffect?: string;
}
/* v8 ignore stop */

const getPageDesignerStyleClasses = ({
    objectFit,
    borderRadius,
    boxShadow,
    padding,
    margin,
    fontWeight,
    letterSpacing,
    hoverEffect,
}: Partial<ProductTileProps>) => {
    const classes: string[] = [];

    if (objectFit) {
        const fitMap = {
            contain: '[&_img]:!object-contain',
            cover: '[&_img]:!object-cover',
            fill: '[&_img]:!object-fill',
            none: '[&_img]:!object-none',
            'scale-down': '[&_img]:!object-scale-down',
        };
        classes.push(fitMap[objectFit]);
    }

    if (borderRadius) {
        const radiusMap: Record<string, string> = {
            none: '!rounded-none',
            xs: '!rounded-xs',
            sm: '!rounded-sm',
            md: '!rounded-md',
            lg: '!rounded-lg',
            xl: '!rounded-xl',
            '2xl': '!rounded-2xl',
            '3xl': '!rounded-3xl',
            '4xl': '!rounded-4xl',
            full: '!rounded-full',
        };
        classes.push(radiusMap[borderRadius] || '!rounded-none');
    }

    if (boxShadow === 'none') {
        classes.push('!shadow-none hover:!shadow-none');
    } else if (boxShadow) {
        const shadowMap = {
            sm: '!shadow-sm hover:!shadow-sm',
            md: '!shadow-md hover:!shadow-md',
            lg: '!shadow-lg hover:!shadow-lg',
            xl: '!shadow-xl hover:!shadow-xl',
            '2xl': '!shadow-2xl hover:!shadow-2xl',
        };
        classes.push(shadowMap[boxShadow]);
    }

    if (padding && padding !== '0') {
        classes.push(`p-${padding}`);
    }

    if (margin && margin !== '0') {
        classes.push(`m-${margin}`);
    }

    if (fontWeight) {
        const weightMap = {
            normal: '[&_a]:!font-normal',
            medium: '[&_a]:!font-medium',
            semibold: '[&_a]:!font-semibold',
            bold: '[&_a]:!font-bold',
        };
        classes.push(weightMap[fontWeight]);
    }

    if (letterSpacing) {
        const spacingMap = {
            tighter: '[&_a]:!tracking-tighter',
            tight: '[&_a]:!tracking-tight',
            normal: '[&_a]:!tracking-normal',
            wide: '[&_a]:!tracking-wide',
            wider: '[&_a]:!tracking-wider',
        };
        classes.push(spacingMap[letterSpacing]);
    }

    if (hoverEffect && hoverEffect !== 'default') {
        const hoverMap = {
            scale: 'hover:!scale-105 !transition-transform !duration-200 hover:!shadow-md',
            shadow: 'hover:!shadow-xl !transition-shadow !duration-200',
            lift: 'hover:!-translate-y-1 hover:!shadow-lg !transition-all !duration-200',
        };
        classes.push(hoverMap[hoverEffect]);
    }

    return classes.join(' ');
};

export interface ProductTileProps extends ComponentProps<'div'> {
    product?: ShopperSearch.schemas['ProductSearchHit'];
    productId?: string;
    maxSwatches?: number;
    handleProductClick?: (product: ShopperSearch.schemas['ProductSearchHit']) => void;
    /** For variant products, filter swatches to only show this variant's color value */
    selectedVariantColorValue?: string | null;
    /** Image aspect ratio (width/height). If provided, calculates height based on viewport width. Defaults to 1 (square) */
    imgAspectRatio?: number;
    /** Show the pickup available indicator icon with tooltip */
    showPickupAvailable?: boolean;
    /** Custom quick add button label */
    quickAddLabel?: string;
    /** Top-level navigation category name shown below swatches (e.g. "Men", "Women") */
    topCategoryName?: string;
    /** Accepted for API compatibility; has no effect */
    showNavigationArrows?: boolean;
    /**
     * When `true`, hides the wishlist heart icon in the top-right of the tile.
     * Used by surfaces like the cart-recommendations carousel where wishlist
     * affordances are intentionally suppressed by design. Default `false`
     * (existing PLP/PDP behavior unchanged).
     */
    disableWishlist?: boolean;
    /**
     * When `true`, hides the Quick Add button that appears on tile hover.
     * Used by surfaces like the cart-recommendations carousel that route the
     * shopper to the PDP for an add-to-cart instead of inline quick-add.
     * Default `false`.
     */
    disableQuickAdd?: boolean;

    // Page Designer styling props
    objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
    borderRadius?: 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | 'full';
    boxShadow?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
    padding?: '0' | '2' | '4' | '6' | '8';
    margin?: '0' | '2' | '4' | '6' | '8';
    fontWeight?: 'normal' | 'medium' | 'semibold' | 'bold';
    letterSpacing?: 'tighter' | 'tight' | 'normal' | 'wide' | 'wider';
    hoverEffect?: 'default' | 'scale' | 'shadow' | 'lift';

    // Page Designer system props (need to be filtered)
    regionId?: string;
    component?: ComponentType;
    componentData?: Record<string, Promise<unknown>>;
    designMetadata?: ComponentDesignMetadata;
    data?: unknown;
}

const ProductTile = forwardRef<HTMLDivElement, ProductTileProps>(
    (
        {
            className,
            product: productProp,
            productId: _productId,
            maxSwatches = PRODUCT_TILE_MAX_SWATCHES,
            selectedVariantColorValue,
            handleProductClick,
            imgAspectRatio,
            showPickupAvailable = false,
            quickAddLabel,
            topCategoryName: _topCategoryName,
            showNavigationArrows: _showNavigationArrows,
            disableWishlist = false,
            disableQuickAdd = false,
            // Page Designer styling props
            objectFit,
            borderRadius,
            boxShadow,
            padding,
            margin,
            fontWeight,
            letterSpacing,
            hoverEffect,
            // Page Designer system props (filter out)
            regionId: _regionId,
            component: _component,
            componentData: _componentData,
            designMetadata: _designMetadata,
            data,
            ...props
        },
        ref
    ) => {
        // Prioritize loader data (Page Designer) over prop (programmatic use)
        const product = (data as ShopperSearch.schemas['ProductSearchHit'] | undefined) || productProp;

        const { config, t, currency, getBadges, swatchMode, triplePlayEligible } = useProductTileContext();

        const productData = useMemo(() => {
            if (!product) return null;
            return {
                badges: getBadges(product),
                rating: getProductRating(product),
            };
        }, [product, getBadges]);

        const effectiveImgAspectRatio = imgAspectRatio ?? config.global.productListing.defaultProductTileImgAspectRatio;

        const isMasterProd = !!product?.variants;
        const isBundleOrSet = product?.productType?.bundle || product?.productType?.set;
        const representedVariant = isMasterProd
            ? product?.variants?.find((variant) => variant?.productId === product?.representedProduct?.id)
            : undefined;
        const defaultVariantPid = isMasterProd && !isBundleOrSet ? (product?.representedProduct?.id ?? null) : null;

        // use the representedVariant values to get a product for PDP
        const initialVariationValue =
            selectedVariantColorValue !== undefined && selectedVariantColorValue !== null
                ? selectedVariantColorValue
                : (representedVariant?.variationValues?.[PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID] ?? undefined);

        // Local swatch selection state — drives image switching and selected ring on swatches.
        // Initialized from the URL-driven prop; updates when the user clicks a swatch on the tile.
        const [selectedAttributeValue, setSelectedAttributeValue] = useState<string | null>(
            initialVariationValue || null
        );

        useEffect(() => {
            if (selectedVariantColorValue !== undefined && selectedVariantColorValue !== null) {
                setSelectedAttributeValue(selectedVariantColorValue);
            }
        }, [selectedVariantColorValue]);

        // Pre-seed every quick-add swatch from the tile's represented variant, with the
        // locally-selected color overriding the represented variant's color when set.
        const initialVariantSelections = useMemo<Record<string, string> | undefined>(() => {
            const representedVariantSelections: Record<string, string> = {
                ...(representedVariant?.variationValues ?? {}),
            };
            if (selectedAttributeValue) {
                representedVariantSelections[PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID] = selectedAttributeValue;
            }
            return Object.keys(representedVariantSelections).length > 0 ? representedVariantSelections : undefined;
        }, [representedVariant, selectedAttributeValue]);

        const variationAttributes = useMemo(
            () => (product ? getDecoratedVariationAttributes(product, { config }) : []),
            [product, config]
        );
        const colorAttributes = variationAttributes.filter(({ id }) => PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID === id);
        // Pass ALL color values to the swatches component — it handles slicing/expansion internally
        const colorValues = (colorAttributes[0]?.values ?? []) as DecoratedVariationAttributeValue[];

        // Legacy `org_ashcomm` `producttile.isml` parity (L155-182): look up the
        // secondary "rollover" image from the variant-aware image groups and render
        // it as an overlay that cross-fades in on hover. `getImagesForColor` filters
        // by `viewType: 'rollover'` so it picks the same image SCAPI returns for the
        // catalog's `<view-type>rollover</view-type>` declaration, scoped to the
        // currently-selected swatch when one is active. When the product has no
        // rollover image (or the SCAPI `imgTypes` filter doesn't include it), the
        // overlay simply doesn't render — graceful fallback to the primary image.
        const rolloverImage = useMemo(() => {
            if (!product) return null;
            const images = getImagesForColor(product, selectedAttributeValue, 'rollover');
            return images[0] ?? null;
        }, [product, selectedAttributeValue]);
        const rolloverImageUrl = rolloverImage?.disBaseLink || rolloverImage?.link;

        const handleSwatchHover = useCallback(
            (value: string) => {
                if (swatchMode === 'hover') {
                    setSelectedAttributeValue(value);
                }
            },
            [swatchMode]
        );
        const handleClick = useCallback(() => {
            product && handleProductClick?.(product);
        }, [handleProductClick, product]);

        const productName = product?.productName ?? '';
        // ## SEO url changes ## — pass productName as slug for /p/{slug}/{id}.html
        const productUrl = createProductUrl(
            product?.productId ?? '',
            null,
            'color',
            defaultVariantPid,
            productName,
            config
        );

        const pageDesignerStyles = getPageDesignerStyleClasses({
            objectFit,
            borderRadius,
            boxShadow,
            padding,
            margin,
            fontWeight,
            letterSpacing,
            hoverEffect,
        });

        if (!product) {
            return (
                <div
                    ref={ref}
                    data-slot="card"
                    className={cn(
                        'text-card-foreground flex flex-col product-card group w-full min-w-0 max-w-full overflow-hidden bg-transparent',
                        pageDesignerStyles,
                        className
                    )}
                    {...props}>
                    <div className="p-4 text-sm text-muted-foreground">{t('selectProduct')}</div>
                </div>
            );
        }

        return (
            <div
                ref={ref}
                data-slot="card"
                data-testid="product-tile"
                className={cn(
                    'text-card-foreground flex flex-col product-card group w-full min-w-0 max-w-full cursor-pointer overflow-hidden bg-transparent',
                    pageDesignerStyles,
                    className
                )}
                {...props}>
                {/* Image area — gallery with soft gradient background and rounded corners (per Figma) */}
                <div className="product-image relative">
                    {/* Scoped `group/tile-image` so the rollover swap fires only when the
                        cursor is over the image area, not the price/swatch metadata below
                        (matches legacy `.product-image:hover` scope from
                        `site_ashleyhomestore_kiosk/.../_product_tiles.scss`). */}
                    <div className="group/tile-image relative w-full overflow-hidden rounded-md">
                        {/* Hover ("rollover") image — legacy `org_ashcomm/producttile.isml`
                            parity (L155-182). Cross-fades a `rollover`-viewType image over
                            the primary on hover. Kept inside the existing anchor in
                            `ProductImageContainer` so a separate click-overlay Link is not
                            needed. `pointer-events-none` preserves the Quick Add / Wishlist
                            buttons stacked above it, and `loading="lazy"` keeps the asset
                            off the LCP critical path. Decorative `alt=""` + `aria-hidden`
                            so screen readers skip it — the primary image's alt already
                            labels the tile. */}
                        <ProductImageContainer
                            product={product}
                            selectedColorValue={
                                PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID === 'color' ? selectedAttributeValue : null
                            }
                            imgAspectRatio={effectiveImgAspectRatio}
                            className="w-full aspect-square"
                            handleProductClick={handleProductClick}
                            rolloverImageUrl={rolloverImageUrl ?? undefined}
                        />

                        <UITarget targetId="sfcc.plp.shipping.deliveryEstimate" />

                        {/* Badges — top-left (Figma white pill with uppercase colored text).
                            Data-driven from `productData.badges` (config.global.badges → tile via
                            `getProductBadges`). When the catalog/recommender feeds no matching
                            badge flag the cluster renders nothing — no placeholder ships. */}
                        {productData?.badges.hasBadges && (
                            <div
                                className="absolute top-3 left-3 flex items-center gap-1.5 z-20"
                                data-testid="product-tile-badges">
                                {productData.badges.badges.map((badge) => (
                                    <Badge
                                        key={badge.label}
                                        variant={
                                            badge.color === 'red' || badge.color === 'orange'
                                                ? 'destructive'
                                                : 'primary'
                                        }
                                        state="secondary"
                                        className="shadow-xs"
                                        data-testid="product-tile-badge">
                                        {badge.label}
                                    </Badge>
                                ))}
                            </div>
                        )}

                        {/* Action icons — top-right */}
                        <div className="absolute top-3 right-3 flex flex-col items-end gap-2 z-20">
                            {showPickupAvailable && (
                                <div>
                                    <div className="group/pickup relative" data-testid="pickup-available-indicator">
                                        <div className="w-9 h-9 p-2 bg-background/90 text-muted-foreground flex items-center justify-center rounded-sm shadow-xs">
                                            <PickupIcon className="w-4 h-4" />
                                        </div>
                                        <div className="absolute right-0 top-full mt-1 z-50 opacity-0 group-hover/pickup:opacity-100 transition-opacity duration-200 pointer-events-none">
                                            <div className="bg-foreground text-background text-xs font-medium px-2 py-1 whitespace-nowrap shadow-lg rounded-sm">
                                                {t('pickupAvailable')}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {!disableWishlist && (
                                <div
                                    className="opacity-100 transition-opacity duration-300"
                                    data-testid="product-tile-wishlist">
                                    <DeferredWishlistButton
                                        product={product}
                                        surface="plp"
                                        size="sm"
                                        tabIndex={-1}
                                        className="relative top-auto right-auto z-20 bg-background/90 hover:bg-background shadow-xs !border-0 rounded-sm"
                                    />
                                </div>
                            )}
                        </div>

                        {/* Hover overlay — subtle dark tint */}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-opacity duration-300 pointer-events-none rounded-md" />

                        {/* Quick Add button */}
                        {!disableQuickAdd && (
                            <div
                                className="absolute bottom-4 left-0 right-0 px-4 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-300 z-20"
                                data-testid="product-tile-quick-add">
                                <QuickAddButton
                                    productId={product.productId ?? ''}
                                    productName={productName}
                                    selectedColorValue={selectedAttributeValue}
                                    initialVariantSelections={initialVariantSelections}
                                    label={quickAddLabel ?? t('quickAdd')}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Info section — no horizontal padding, only top spacing (matches Figma) */}
                <div className="relative pt-3 flex flex-col gap-2 items-start">
                    {/* Inner block — descriptive flags, product name, description, and price (4px gap) */}
                    <div className="flex flex-col gap-1 items-start w-full">
                        {/* Product name — the single keyboard/SR tab stop for this tile */}
                        <h3 className="text-sm font-semibold leading-5 text-card-foreground">
                            <Link
                                to={productUrl}
                                className="hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-none"
                                onClick={handleClick}>
                                {productName}
                            </Link>
                        </h3>

                        {/* Price — red current price, struck-through MSRP, uppercase promo callout.
                            `showPerUnitSubtitle` opts the tile into legacy `org_ashcomm`
                            `producttile_pricing.isml` parity: when the product is a "set of
                            multiples" (`minOrderQuantity > 1 && minOrderQuantity === stepQuantity`)
                            the headline price multiplies up by `minOrderQuantity` and a small
                            `($<unit> each)` subtitle renders below the price. No-op for the
                            common case where a product has `minOrderQuantity === 1`. */}
                        <ProductPrice
                            type="unit"
                            product={product}
                            currency={currency ?? config.commerce.sites?.[0]?.defaultCurrency ?? ''}
                            labelForA11y={(product?.productName ?? product?.productId) || ''}
                            currentPriceProps={{
                                className: 'text-sm leading-5 !font-normal',
                            }}
                            listPriceProps={{
                                className: 'text-muted-foreground text-sm leading-5 line-through ml-1.5',
                            }}
                            promoCalloutProps={{
                                className:
                                    'text-xs font-semibold leading-4 text-destructive uppercase tracking-[1.8px] mt-1',
                            }}
                            className="flex items-start text-sm gap-1.5"
                            unavailablePriceContent={<CheckPriceCta />}
                            showPerUnitSubtitle
                        />

                        {/* "Regular Price Learn more" — legacy `org_ashcomm` Triple Play caption.
                            Gated by site `isTriplePlay` toggle + shopper zone in
                            `priceZoneList` (both folded into `_app` loader's
                            `triplePlayEligible`) + per-product strike-through. The
                            shared `<TriplePlayCaption>` keeps the PLP tile and the
                            PDP price block (`src/components/product-view/product-info.tsx`)
                            rendering the exact same caption + modal. */}
                        <TriplePlayCaption
                            product={product}
                            currency={currency}
                            productName={productName}
                            triplePlayEligible={triplePlayEligible}
                            data-testid="product-tile-regular-price"
                        />
                    </div>

                    {/* Color swatches */}
                    {colorValues.length > 0 && (
                        <Suspense fallback={<ProductTileSwatchesSkeleton count={maxSwatches} />}>
                            <LazySwatches
                                colorValues={colorValues}
                                selectedAttributeValue={selectedAttributeValue}
                                onSwatchHover={handleSwatchHover}
                                onSwatchClick={handleClick}
                                productName={productName}
                                totalColorCount={colorAttributes[0]?.values?.length ?? colorValues.length}
                                maxSwatches={maxSwatches}
                                productHref={productUrl}
                            />
                        </Suspense>
                    )}
                    {/* Star ratings — 18px stars matching Figma "Star Reviews" (gap-0.5 between stars,
                        gap-2 between star group and the review count link). */}
                    <StarRating
                        rating={productData?.rating.rating ?? 0}
                        reviewCount={productData?.rating.reviewCount ?? 0}
                        starSize="md"
                        showRatingLink
                        ratingLinkTemplate="({count})"
                        ratingLinkClassName="text-xs text-muted-foreground"
                        className="w-full"
                    />
                    <UITarget targetId="sfcc.productCard.reviews.rating" />

                    <UITarget targetId="sfcc.productCard.loyalty.points" />
                    <UITarget targetId="sfcc.productCard.bnpl.message" />
                </div>
            </div>
        );
    }
);

ProductTile.displayName = 'ProductTile';

// eslint-disable-next-line react-refresh/only-export-components
export const loader = loaders.server;
export { ProductTile };
// eslint-disable-next-line react-refresh/only-export-components
export { ProductTileProvider, useProductTileContext } from './context';
export default ProductTile;
